import React from 'react'
import clsx from 'clsx'
import { useHistory, useRouteMatch } from 'react-router'
import { useDrag, useDrop } from 'react-dnd'
import { Row } from '@tanstack/react-table'
import { ArrowRight2 } from 'iconsax-react'
import { motion } from 'framer-motion'
import { useTheme } from '@mui/material'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { selectCustomFields } from '@SonarCX/slices/customFields'

import MobileDefault from '../../Cells/MobileDefault'
import TaskRowRightClickMenu from '@SonarCX/components/molecules/Table/Components/TaskRowRightClickMenu'
import DragIcon from '@SonarCX/components/icons/DragIcon'
import ShareTaskModal from '@SonarCX/components/molecules/ShareTaskModal'

import { Routes } from '@SonarCX/Routes'
import { getCell } from './helpers'
import { useTaskPageState } from '@SonarCX/providers/TaskPageStateProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import useCombinedTableState from '@SonarCX/hooks/useCombinedTableState'
import { TYPES, useTreeState } from '@SonarCX/providers/TreeStateProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useTaskRightClickMenu from '@SonarCX/hooks/useTaskRightClickMenu'

import useStyles from './styles'

type SingleSubtaskProps = {
  row: Row<LocalTask>
  index: number
  onDragDrop: (sourceID: string, targetID: string, after: boolean) => void
  dragDropID: string
  task: APITask
  targetRef: React.MutableRefObject<any>
  onMove: (id: string, tasks: APITask[]) => void
  expanded: string[]
  setExpanded: React.Dispatch<React.SetStateAction<string[]>>
  smallMedia: boolean
  depth: number
  onSubMove: (id: string) => void
  observerId: string
  enableCustomContextMenu?: boolean
}

const SingleSubtask: React.FC<SingleSubtaskProps> = ({
  row,
  index,
  onDragDrop,
  dragDropID,
  task,
  targetRef,
  onMove,
  expanded,
  setExpanded,
  smallMedia,
  depth,
  onSubMove,
  observerId,
  enableCustomContextMenu
}) => {
  const theme = useTheme()
  const history = useHistory()
  const { path } = useRouteMatch()
  const { customFields } = useAppSelector(selectCustomFields)
  const { lastViewed, tab } = useTaskPageState()
  const { updatedTask } = useTaskAPI()
  const { tasks, columnOrder } = useCombinedTableState()
  const { dispatch: treeDispatch, Actions: TreeActions } = useTreeState(
    TYPES.TASK
  )
  const { isSmall } = useBreakpoint()

  const numCells = React.useMemo(() => row.getVisibleCells().length, [row])

  const taskCfs = React.useMemo(
    () =>
      customFields.filter(
        x => x.object_type === 'task' && x.field_type !== 'table'
      ),
    [customFields]
  )

  // Filter out columns on user and contact details
  const order = React.useMemo(() => {
    if (!['/customers/:id', '/users/:id'].includes(path)) {
      return columnOrder
    }

    return columnOrder.filter(
      item => !['updated_at', 'assignee_ids'].includes(item)
    )
  }, [path, columnOrder])

  const onDrop = React.useCallback(
    (after: boolean) => (droppedItem: any) => {
      setHovering(false)

      const sourceID = droppedItem.id
      const targetID = task.id
      if (sourceID === targetID) {
        return
      }

      onDragDrop(sourceID, targetID, after)
    },
    [task.id, onDragDrop]
  )

  const ITEM_TYPE = `SUB_TASK_${dragDropID}`

  const [{ opacity }, dragRef, previewRef] = useDrag({
    item: { type: ITEM_TYPE, id: task.id },
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.4 : 1
    })
  })

  const [{ isOver: isOverTop }, dropTop] = useDrop({
    accept: ITEM_TYPE,
    drop: onDrop(false),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [{ isOver: isOverBottom }, dropBottom] = useDrop({
    accept: ITEM_TYPE,
    drop: onDrop(true),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [hovering, setHovering] = React.useState(false)

  const {
    setShowShare,
    handleOpen,
    handleClose,
    showShare,
    menuPosition,
    showMenu
  } = useTaskRightClickMenu()

  const classes = useStyles({ depth, isOverTop, isOverBottom })

  const startHovering = React.useCallback(() => setHovering(true), [])

  const endHovering = React.useCallback(() => setHovering(false), [])

  const handleRightClick = e => {
    if (!enableCustomContextMenu) return
    e.preventDefault()
    handleOpen(e)
  }

  const handleRightClickClose = () => {
    handleClose()
  }
  // Moves to task details
  const handleClick = React.useCallback(
    e => {
      const classes = e.target.classList

      if (classes.contains('td')) {
        const taskID = task.id
        onMove(taskID, tasks)
        history.push(`${Routes.TASKS}/${taskID}`)
      }
    },
    [onMove, task.id, history, tasks]
  )

  // Toggle expansion and update tree state
  const toggleExpansion = React.useCallback(
    (id: string) => {
      setExpanded(old => {
        if (old.includes(id)) {
          treeDispatch({ type: TreeActions.UPDATE, id, open: false })
          return old.filter(ids => ids !== id)
        } else {
          treeDispatch({ type: TreeActions.UPDATE, id, open: true })
          return [...old, id]
        }
      })
    },
    [setExpanded, treeDispatch, TreeActions.UPDATE]
  )

  const cells = React.useMemo(() => {
    let shouldSkip = false
    const cells = {}

    row.getVisibleCells().forEach((_, j) => {
      // Fallback
      cells[j] = () => null
      // Find correct components to show
      let Cell = getCell({
        index: j + (shouldSkip ? 1 : 0),
        order,
        tab,
        observerId,
        taskCfs
      })
      if (Cell === null) {
        shouldSkip = true
        Cell = getCell({
          index: j + (shouldSkip ? 1 : 0),
          order,
          tab,
          observerId,
          taskCfs
        })
      }

      // Store for later, also prevents unnnecessary re-renders
      cells[j] = Cell
    })
    return cells
  }, [row, order, tab, observerId, taskCfs])

  if (smallMedia) {
    return (
      <motion.tr
        key={index}
        ref={task.id === lastViewed ? targetRef : undefined}
        className={clsx(
          'table-row',
          classes.row,
          task.id === lastViewed && 'blink',
          task.id === updatedTask?.id && 'updated-row'
        )}
        onClick={e => handleClick(e)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <td
          colSpan={numCells}
          className='table-data'
          style={{ display: 'flex', paddingLeft: `${depth}rem` }}
        >
          {task.sub_tasks > 0 ? (
            <ArrowRight2
              size={theme.size.sm}
              onClick={() => toggleExpansion(task.id)}
              className={clsx(
                'arrow',
                expanded.includes(task.id) && 'arrow-down',
                isSmall && 'center-arrow'
              )}
            />
          ) : (
            <div
              className='arrow'
              style={{ cursor: 'default', width: '0.5rem' }}
            />
          )}

          <MobileDefault task={task} onMove={onSubMove} />
        </td>
      </motion.tr>
    )
  }

  return (
    <>
      {index === 0 && (
        <tr>
          <td
            ref={dropTop}
            colSpan={numCells}
            className={clsx(classes.drop, classes.dropTop)}
          />
        </tr>
      )}

      <motion.tr
        key={task.id}
        ref={el => {
          if (task.id === lastViewed && targetRef) {
            targetRef.current = el
          }
          previewRef(el)
        }}
        onMouseEnter={startHovering}
        onMouseLeave={endHovering}
        onContextMenu={handleRightClick}
        onClick={handleClick}
        className={clsx(
          'table-row',
          classes.row,
          task.id === lastViewed && 'blink',
          task.id === updatedTask?.id && 'updated-row'
        )}
        style={{ opacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {row.getVisibleCells().map((_, j) => {
          const Cell = cells[j]

          return (
            <td
              key={`data-${j}`}
              className='table-data'
              style={
                j === 0
                  ? {
                      display: 'flex',
                      paddingRight: '0.5rem',
                      whiteSpace: 'nowrap',
                      paddingLeft: `${depth}rem`,
                      transform: 'translateY(50%)'
                    }
                  : {}
              }
            >
              {j === 0 && (
                <>
                  {hovering ? (
                    <div ref={dragRef} className={classes.drag}>
                      <DragIcon />
                    </div>
                  ) : (
                    <div className={classes.emptyDrag} />
                  )}

                  {task.sub_tasks > 0 ? (
                    <ArrowRight2
                      size={theme.size.sm}
                      onClick={() => toggleExpansion(task.id)}
                      className={clsx(
                        'arrow',
                        expanded.includes(task.id) && 'arrow-down',
                        isSmall && 'center-arrow'
                      )}
                    />
                  ) : (
                    <div
                      className='arrow'
                      style={{ cursor: 'default', width: '1rem' }}
                    />
                  )}
                </>
              )}

              <div
                style={j === 0 ? { width: '100%' } : { padding: '0 0.5rem' }}
              >
                <Cell task={task} onMove={onSubMove} />
              </div>
            </td>
          )
        })}
      </motion.tr>

      <tr>
        <td
          ref={dropBottom}
          colSpan={numCells}
          className={clsx(classes.drop, classes.dropBottom)}
        />
      </tr>

      {showMenu && (
        <TaskRowRightClickMenu
          menuPosition={menuPosition}
          handleClose={handleRightClickClose}
          handleShare={() => setShowShare(true)}
          task={task}
        />
      )}
      {showShare && (
        <ShareTaskModal
          selected={task.id}
          handleOnClose={() => setShowShare(false)}
        />
      )}
    </>
  )
}

export default React.memo(SingleSubtask)
